'use client';
import { FC, RefObject, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { SOLVED_FLAP, solvedFlapFaces } from '@puzzlio/core';
import { cn } from '@/app/lib/utils';

/** "Over 16.4 million puzzles solved", each digit on its own tile; `millions` is from `solvedMillions`. */
export const SolvedCounter: FC<{ millions: string; className?: string }> = ({ millions, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const faces = useMemo(() => solvedFlapFaces(millions), [millions]);
  const step = useFlapStep(ref, SOLVED_FLAP.flips + 1);

  let tile = 0;
  return (
    <div
      ref={ref}
      className={cn('font-quicksand bg-yellow flex flex-col items-center justify-center gap-2 py-7', className)}
    >
      <span className="text-xl font-bold">OVER</span>
      {/* Baseline-aligned, so the point and "million" sit on the digits' baseline wherever the tiles put it. */}
      <div className="font-chinook flex items-baseline gap-1.5 text-4xl leading-none" aria-hidden>
        {[...millions].map((char, index) => {
          if (char === '.') return <span key={index}>.</span>;
          const own = faces[tile++];
          const face = own[Math.min(step, own.length - 1)];
          const previous = own[Math.max(0, Math.min(step - 1, own.length - 1))];
          return <FlapTile key={index} face={face} previous={previous} />;
        })}
        <span className="ml-1.5">million</span>
      </div>
      <span className="sr-only">{millions} million</span>
      <span className="text-xl font-bold">PUZZLES SOLVED</span>
    </div>
  );
};

/**
 * How many flips the board has made, out of the `length` faces each tile shows: none until the counter is
 * mostly in view, then one every `flipMs` until the count has landed. Straight to the end for anyone who
 * prefers reduced motion.
 */
function useFlapStep(ref: RefObject<HTMLElement | null>, length: number): number {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStep(length);
      return;
    }
    let timer: ReturnType<typeof setInterval> | undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        timer = setInterval(() => {
          setStep((current) => {
            if (current + 1 >= length) clearInterval(timer);
            return current + 1;
          });
        }, SOLVED_FLAP.flipMs);
      },
      { threshold: 0.6 }
    );
    observer.observe(ref.current!);
    return () => {
      observer.disconnect();
      clearInterval(timer);
    };
  }, [ref, length]);

  return step;
}

const TILE = 'h-[52px] w-[42px] rounded-md bg-white';

/** A digit trimmed to cap height and baseline, centred in a tile-sized box. */
function Digit({ char, className }: { char: string; className?: string }) {
  return (
    <span className={cn('flex items-center justify-center', TILE, className)}>
      {/* Trimmed so centring centres the digit itself: Chinook's line box has room below the baseline
          that would otherwise lift it. */}
      <span className="[text-box:trim-both_cap_alphabetic]">{char}</span>
    </span>
  );
}

/** One half of a tile, showing that half of `char`. */
function Half({
  char,
  top,
  className,
  ref,
}: {
  char: string;
  top: boolean;
  className?: string;
  ref?: RefObject<HTMLSpanElement | null>;
}) {
  return (
    <span
      ref={ref}
      className={cn(
        'absolute inset-x-0 h-1/2 overflow-hidden bg-white',
        top ? 'top-0 rounded-t-md' : 'bottom-0 rounded-b-md',
        className
      )}
    >
      <Digit char={char} className={cn('absolute inset-x-0', top ? 'top-0' : 'bottom-0')} />
    </span>
  );
}

/**
 * A digit tile, turning from `previous` to `face` when they differ: the old digit's top half folds down
 * over the new one, then the new digit's bottom half lands over the old. The digit underneath stays in
 * the flow throughout, so the tile keeps the baseline the row aligns on.
 */
function FlapTile({ face, previous }: { face: string; previous: string }) {
  return (
    <span className={cn('relative [perspective:300px]', TILE)}>
      <Digit char={face} />
      {face !== previous && <Flip key={`${previous}${face}`} from={previous} to={face} />}
    </span>
  );
}

function Flip({ from, to }: { from: string; to: string }) {
  const topFlap = useRef<HTMLSpanElement>(null);
  const bottomFlap = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const half = SOLVED_FLAP.flipMs / 2;
    topFlap.current!.animate(
      [
        { transform: 'rotateX(0deg)', filter: 'brightness(1)' },
        { transform: 'rotateX(-90deg)', filter: 'brightness(0.8)' },
      ],
      { duration: half, easing: 'ease-in', fill: 'forwards' }
    );
    bottomFlap.current!.animate(
      [
        { transform: 'rotateX(90deg)', filter: 'brightness(0.8)' },
        { transform: 'rotateX(0deg)', filter: 'brightness(1)' },
      ],
      { duration: half, delay: half, easing: 'ease-out', fill: 'both' }
    );
  }, []);

  return (
    <>
      <Half char={from} top={false} />
      {/* Hinged on the tile's middle. */}
      <Half ref={topFlap} char={from} top className="origin-bottom" />
      <Half ref={bottomFlap} char={to} top={false} className="origin-top" />
    </>
  );
}
